/**
 * Device-local storage. The complete list of what the portal writes to a browser.
 *
 * Never stored on a device: the ID token (SDK memory only), any email address, any orgId,
 * any API key or Stripe URL, any policy document, any usage number. The query cache is not
 * persisted either — a disk-cached invoice list on a shared machine is a leak with no upside.
 */

export const STORAGE_KEYS = {
  /**
   * Owned by @promptgatewaysadmin/ui and SHARED WITH THE EXTENSION, which is what makes the
   * Settings promise "Match the extension's light or dark theme" true.
   *
   * [CORRECTION] docs/ENV_AND_SECRETS.md §4 names this key `pg.theme`. The library's actual
   * key is `pg-theme` (Design/ui/src/theme/storage.ts), and the library is the side that
   * has to match the extension. Using the doc's spelling would have quietly broken the
   * sharing the row exists to provide.
   */
  theme: "pg-theme",
  reduceMotion: "pg.reduceMotion",
  keepSignedIn: "pg.keepSignedIn",
  /** sessionStorage. Survives signup -> verify -> onboarding -> checkout without a URL. */
  funnel: "pg.funnel",
} as const;

function read(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    // Private mode / partitioned storage. A preference we cannot read is a preference we
    // fall back on, never an error the user has to see.
    return null;
  }
}

function write(store: Storage | undefined, key: string, value: string): void {
  try {
    store?.setItem(key, value);
  } catch {
    /* ignore */
  }
}

const local = () => (typeof window === "undefined" ? undefined : window.localStorage);
const session = () => (typeof window === "undefined" ? undefined : window.sessionStorage);

/** Defaults to true — the checkbox ships checked, and this is the per-device choice. */
export function readKeepSignedIn(): boolean {
  return read(local(), STORAGE_KEYS.keepSignedIn) !== "false";
}

export function writeKeepSignedIn(value: boolean): void {
  write(local(), STORAGE_KEYS.keepSignedIn, String(value));
}

export function readReduceMotion(): boolean {
  return read(local(), STORAGE_KEYS.reduceMotion) === "true";
}

export function writeReduceMotion(value: boolean): void {
  write(local(), STORAGE_KEYS.reduceMotion, String(value));
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("pg-reduce-motion", value);
  }
}

export interface Funnel {
  plan?: string;
  interval?: string;
}

/**
 * The plan/interval pair from the marketing CTA.
 *
 * Held in sessionStorage rather than the URL: no commercial intent in a URL the browser
 * history keeps, and losing it would drop a customer who already chose onto a generic
 * pricing screen.
 */
export function readFunnel(): Funnel {
  const raw = read(session(), STORAGE_KEYS.funnel);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Funnel;
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function writeFunnel(funnel: Funnel): void {
  const clean: Funnel = {};
  if (funnel.plan) clean.plan = funnel.plan;
  if (funnel.interval) clean.interval = funnel.interval;
  if (Object.keys(clean).length === 0) return;
  write(session(), STORAGE_KEYS.funnel, JSON.stringify(clean));
}

export function clearFunnel(): void {
  try {
    session()?.removeItem(STORAGE_KEYS.funnel);
  } catch {
    /* ignore */
  }
}
